/**
 * 브랜드메시지(카카오 브랜드 메시지, FREE형) 정책 헬퍼.
 *
 * 값의 단일 기준은 sales-server/docs/brand-message-issue.md §5
 * "유형별 공식 규격"(비즈뿌리오 원문 전수 조사)이다. 조회 API 가 없어서 프론트가
 * 같은 숫자를 들고 있어야 fail-fast 가 성립한다. 규격이 바뀌면 문서 → 여기 순서.
 *   단가        sales-server/src/credit.py METHOD_CREDIT[brandmessage]
 *   발송창      sales-server/src/daou.py   BRANDMESSAGE_SEND_WINDOW
 *   검증 규칙   sales-server/src/campaign.py _validateBrandMessageCampaign (§6-1 에러 키)
 *   이미지 규격 sales-server/handler.py    BRANDMESSAGE_IMAGE_RATIO
 *   대상/kind   sales-server/src/daou.py   BRANDMESSAGE_* 상수
 *
 * 계약 gotcha
 *  - (광고) 표기와 무료수신거부 번호는 발송 직전 서버가 합성한다(daou.applyBrandMessageAdMarker).
 *    저장 본문에 절대 넣지 말 것. 여기서는 미리보기 문자열과 실효 상한 계산만 한다.
 *  - 화면 유형은 ut·ui·ul·uc 4종이다(BM-26 (A)안). 서버의 uw(76자)는 PM 규격에 없어
 *    선택 목록에서 제외한다 — 상수에는 남겨 두되 hidden 이다.
 *  - imageType(normal/wide)은 친구톡 잔재이므로 브랜드에 쓰지 말 것.
 *  - brandImages 는 서버의 KAPI 업로드 캐시다. 프론트는 읽지도 쓰지도 않는다.
 *  - 쿠폰은 버튼이 아니라 네이티브 오브젝트다(§5). buttons 배열에 넣지 않는다.
 *    본문형·ul 은 campaign.coupon 하나 + 연결 쿠폰 campaign.couponId·couponProvider.
 *    캐러셀(uc)은 cards[i].coupon 으로 카드마다 따로 두고(카카오
 *    carousel.list[i].attachment.coupon) 연결 쿠폰도 그 오브젝트 안에 카드별로 싣는다
 *    (cards[i].coupon.couponId·couponProvider, BM-86) — "쿠폰 — 네이티브 오브젝트" 절.
 *  - listItems/cards/tail/coupon 은 AWSJSON 이라 저장 시 JSON.stringify, 로드 시
 *    JSON.parse 가 필요하다. 이 모듈은 항상 파싱된 객체를 다룬다(직렬화는 API 계층 책임).
 */

/** Campaign.method 서버 enum 값. 에디터 로컬 키("brand")와 다르다. */
export const CAMPAIGN_METHOD = "brandmessage";

/** Campaign.brandMessageType 고정값. 서버가 wire 에 FREE 를 하드코딩한다. */
export const BRAND_MESSAGE_TYPE = "FREE";

/** 마케팅 대상 — 발신프로필 브랜드메시지 승인 + 080 번호가 있어야 발송된다. */
const MARKETING_TARGETS = ["M", "N", "O"];

const SEND_TARGET_KEYS = ["friend", ...MARKETING_TARGETS];

/**
 * 건당 **캔디** 단가. 발송 대상 × 말풍선 유형으로 갈린다(BM-69 확정 정책).
 * 마케팅 대상(M·N·O)은 유형과 무관하게 6이고, 친구(I형)만 유형별로 다르다.
 * 시안·계약표의 15·17·23 은 원 단위 표기라 캔디로 쓰면 안 된다(1캔디=5원, 올림).
 * 여기서 friend 는 카카오 채널 친구(I형)이며 종료된 친구톡과 무관하다.
 */
export const CREDIT_COST = {
  friend: { ut: 4, ui: 5, uw: 5, ul: 5, uc: 6 },
  marketing: 6,
};

/** 친구 단가의 최소·최대 — 유형 미선택 구간의 범위 표기("4~6캔디")에 쓴다. */
export const FRIEND_COST_RANGE = [
  Math.min(...Object.values(CREDIT_COST.friend)),
  Math.max(...Object.values(CREDIT_COST.friend)),
];

// 브랜드 건당 단가 범위 표기("4~6캔디"). 대상 × 말풍선 유형으로 갈리는 단가를
// 한 줄로 줄인 값이라 CREDIT_COST 에서 파생시킨다 — 하드코딩하면 단가 개정 때 어긋난다.
// 하한은 친구 최저(ut), 상한은 친구 최고와 마케팅 중 큰 쪽이다.
export const BRAND_COST_RANGE_TEXT = `${FRIEND_COST_RANGE[0]}~${Math.max(
  FRIEND_COST_RANGE[1],
  CREDIT_COST.marketing,
)}캔디`;

/** 광고성 메시지 발송 가능 시간(KST). 경계 포함. 자동은 태그 지연 1시간을 뺀다. */
export const SEND_WINDOW = {
  manual: { start: "08:00", end: "20:50" },
  auto: { start: "08:00", end: "19:50" },
};

/** 비즈뿌리오 예약(sendtime) 상한. */
export const RESERVE_MAX_DAYS = 30;

export const IMAGE_FORMATS = ["jpg", "jpeg", "png"];
export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png"];
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_MIN_WIDTH = 500;

/**
 * 웹 링크 공통 상한. §5 가 500자를 명시한 건 이미지 클릭 링크(imageLink) 하나뿐이고
 * (§5 이미지 표) 리스트 항목·더보기·쿠폰·버튼 PC 링크에는 §5 에도 서버에도 상한
 * 규정이 없다 — 같은 비즈뿌리오 URL 필드라 이 값을 공통으로 쓴다. 규격이 갈리면
 * 그때 필드별로 나눠라.
 */
export const LINK_MAX_LENGTH = 500;

/**
 * 버튼 타입.
 *  - WL 웹링크 / AL 앱링크 : linkMo 필수
 *  - AC 채널추가          : sendTarget 이 M·N 일 때만 허용
 * 쿠폰은 버튼이 아니다(§5) — campaign.coupon 오브젝트라 이 목록에 없고 이름 상한과
 * 무관하다(개수는 ut·ui 에서 쿠폰 ON 시 −1 — BM-78 getButtonMax). BM-29 이전에 저장된
 * `{type:"coupon"}` 항목은 검증·페이로드 양쪽에서 조용히 떨군다
 * (normalizeButtons·validateButtons).
 * NOTE: CampaignButton.target(inapp|out)은 저장은 되지만 브랜드 wire 에 실리지 않는다.
 */
const BUTTON_TYPES = ["WL", "AL", "AC"];

/** AC(채널추가) 버튼명은 카카오 규격상 고정 문구다(BM-41 ①). */
export const CHANNEL_ADD_BUTTON_NAME = "채널 추가";

// ─────────────────────────────────────────────────────────────
// 쿠폰 — 네이티브 오브젝트 (§5)
// ─────────────────────────────────────────────────────────────

/**
 * 저장 shape 은 `{type, value, description, linkMo, linkPc, schemeAndroid, schemeIos,
 * couponId, couponProvider}` 오브젝트다. 자리는 유형에 따라 다르다.
 *  - 본문형·ul : campaign.coupon 하나. 연결 쿠폰은 캠페인 레벨 campaign.couponId·
 *    couponProvider 다(오브젝트 안의 두 키는 여기서는 쓰지 않는다 — null 로 나간다).
 *  - 캐러셀(uc): cards[i].coupon — 카드마다 독립(카카오 BizMessage·비즈뿌리오 규격 모두
 *    carousel.list[i].attachment.coupon 이고 "메시지 전체 1개" 제약이 없다). 연결 쿠폰도
 *    **카드별**이다(cards[i].coupon.couponId·couponProvider, BM-86) — 카드마다 다른 쿠폰을
 *    발급·연결할 수 있어야 한다(PM 확정). 캠페인 레벨 campaign.coupon 은 항상 null 로
 *    싣고(BM-85), campaign.couponId·couponProvider 는 **전환기 이중
 *    기록**으로 연결 쿠폰이 있는 첫 카드의 값을 싣는다(buildBrandMessagePayload) — 서버가
 *    카드별 발급을 배포하기 전까지 그 하나를 발급해 전 카드에 같은 링크를 주는 종전 동작을
 *    유지하기 위해서다. 서버 배포 후 이중 기록은 제거한다.
 *    gotcha(서버 BM-86 배포 전): 서버는 campaign.couponId 하나만 발급해 쿠폰을 켠 카드
 *    전부에 그 링크를 싣는다 — 카드마다 다른 쿠폰을 연결하면 배포 전까지는 두 번째 쿠폰
 *    링크가 첫 쿠폰 링크로 나간다. 프론트에서 막지 않는다(카드별 쿠폰이 요구사항, PM 결정).
 *    옛 저장분(campaign.coupon·couponId 만 있고 카드 쿠폰에 couponId 가 없음)은 로드
 *    (hydrateBrandCoupon)가 카드로 옮긴다 — 폼을 거치지 않은 레코드를 검증하는
 *    호출부(캠페인 목록 게이트)도 먼저 태워야 한다.
 *  - 캐러셀에서 캠페인 레벨 couponId·couponProvider 는 **읽지 않는다**(검증·페이로드·
 *    getLinkedCoupons 모두 카드만 본다) — 폼 편집 중에는 페이로드를 만들기 전이라 카드와
 *    어긋나 있고, 연결을 전부 풀었을 때 이중 기록 잔류값이 되살아나면 안 된다.
 *
 *  - title 은 프론트가 만들지 않는다. type+value 로 서버가 아래 5패턴 중 하나를
 *    조립한다 — buildTitle 은 미리보기·안내 전용이다.
 *  - 자유 문구가 불가능하므로 버튼명 글자수 제약(세로 14 / 가로 8)을 받지 않는다.
 *  - linkMo 는 필수지만 couponId(연결 쿠폰)가 있으면 서버가 수신자별 쿠폰 링크로
 *    채운다. §5 "사용자 지정 쿠폰 링크는 경로 없음" — 실무 경로는 쿠폰 연결이다.
 */

/** free·up 의 value 는 상품명이다(§5). */
const COUPON_PRODUCT_NAME_MAX = 7;

/**
 * title 에 실릴 금액 표기. 서버·비즈뿌리오 규격은 `{숫자}원 할인 쿠폰` **5패턴 고정**이고
 * 공식 예시가 "1원"·"10원" 이라 자릿수 콤마가 들어가면 거부될 수 있다 → 미리보기가
 * 서버 조립 문구와 글자 단위로 같도록 콤마 없이 숫자만 쓴다(BM-45, §5 쿠폰).
 * 입력 필드의 읽기용 콤마는 화면 몫이다 — 여기서는 title 조립만 본다.
 */
const formatCouponAmount = (value) =>
  String(value ?? "")
    .replace(/,/g, "")
    .trim();

/**
 * value.kind 가 입력 위젯을 가른다 — number(범위) / text(글자수) / none(값 없음).
 * sampleValue 는 유형 선택 화면에서 buildTitle 로 예시를 만들 때 쓴다.
 */
export const COUPON_TYPES = [
  {
    key: "discountAmount",
    label: "금액 할인",
    value: { kind: "number", min: 1, max: 99999999, unit: "원" },
    valueLabel: "할인 금액",
    valuePlaceholder: "3000",
    sampleValue: 3000,
    buildTitle: (value) => `${formatCouponAmount(value)}원 할인 쿠폰`,
  },
  {
    key: "discountRate",
    label: "할인율",
    value: { kind: "number", min: 1, max: 100, unit: "%" },
    valueLabel: "할인율",
    valuePlaceholder: "10",
    sampleValue: 10,
    buildTitle: (value) => `${value}% 할인 쿠폰`,
  },
  {
    key: "shipping",
    label: "배송비",
    value: { kind: "none" },
    valueLabel: null,
    valuePlaceholder: null,
    sampleValue: null,
    buildTitle: () => "배송비 할인 쿠폰",
  },
  {
    key: "free",
    label: "상품 무료",
    value: { kind: "text", maxLength: COUPON_PRODUCT_NAME_MAX },
    valueLabel: "상품명",
    valuePlaceholder: "아메리카노",
    sampleValue: "아메리카노",
    buildTitle: (value) => `${value} 무료 쿠폰`,
  },
  {
    key: "up",
    label: "상품 UP",
    value: { kind: "text", maxLength: COUPON_PRODUCT_NAME_MAX },
    valueLabel: "상품명",
    valuePlaceholder: "아메리카노",
    sampleValue: "아메리카노",
    buildTitle: (value) => `${value} UP 쿠폰`,
  },
];

const COUPON_TYPE_BY_KEY = COUPON_TYPES.reduce((obj, type) => {
  obj[type.key] = type;
  return obj;
}, {});

export const getCouponType = (couponType) =>
  COUPON_TYPE_BY_KEY[couponType] || null;

/** 값이 비었는지. 0 은 어차피 범위 밖이라 빈 값과 같이 취급해도 판정이 같다. */
const isBlankValue = (value) =>
  value == null || String(value).trim() === "";

/**
 * 말풍선에 찍힐 title 미리보기. 유형이 없거나 값이 덜 채워졌으면 "" 다.
 * 실제 wire 문자열은 서버가 만든다(여기 값은 화면 안내 전용).
 */
export const getCouponTitlePreview = (coupon) => {
  const type = getCouponType(coupon?.type);
  if (!type) return "";
  if (type.value.kind === "none") return type.buildTitle();
  if (isBlankValue(coupon?.value)) return "";
  return type.buildTitle(String(coupon.value).trim());
};

/** 유형 선택 화면용 예시 title("3000원 할인 쿠폰" — 콤마 없음, BM-45). */
export const getCouponTitleSample = (couponType) => {
  const type = getCouponType(couponType);
  return type ? type.buildTitle(type.sampleValue) : "";
};

/** 쿠폰 오브젝트로 볼 만한 입력이 하나라도 있는가(빈 폼과 구분). */
const COUPON_FIELDS = [
  "type",
  "value",
  "description",
  "linkMo",
  "linkPc",
  "schemeAndroid",
  "schemeIos",
];

/**
 * 본문형·ul 의 화면 토글은 `coupon || couponId` 로 켜진다(BrandMessageEditor.isCouponOn).
 * couponId 를 입력으로 안 치면 "연결 쿠폰만 있고 유형 미선택" 상태에서 토글은 ON 인데
 * 검증이 통째로 스킵된다 → 토글 ON = 유형 필수(BM-49).
 * 본문형·ul 의 couponId 는 캠페인 레벨 필드라 두 번째 인자로 받는다. 캐러셀 카드 쿠폰은
 * 연결 쿠폰을 오브젝트 안에 싣고 있어(cards[i].coupon.couponId, BM-86) 인자 없이 부르면
 * 그 값을 본다 — 연결만 있고 유형이 없는 카드도 "켜짐"이라 유형 필수 검증을 받는다.
 */
export const hasCouponInput = (coupon, couponId = coupon?.couponId) =>
  !isBlankValue(couponId) ||
  (!!coupon && COUPON_FIELDS.some((field) => !isBlankValue(coupon[field])));

/**
 * 로드 하이드레이션 — 카드별 연결 쿠폰 계약(BM-86) 이전에 저장된 캐러셀을 새 계약으로
 * 옮긴다. 옛 저장분의 판정은 "campaign.coupon 또는 campaign.couponId 가 있고 카드 쿠폰에
 * couponId 가 하나도 없음"이다.
 *  - campaign.coupon(BM-85 이전, 쿠폰 오브젝트가 캠페인에 하나)은 **쿠폰을 켠 첫 카드**
 *    (없으면 첫 카드)에만 넣는다. 옛 서버는 전 카드에 복제해 보냈지만, 그대로 복사하면
 *    사용자가 넣은 적 없는 카드에 쿠폰이 "이미 들어가 있는" 상태가 돼 카드별 쿠폰 요구와
 *    어긋난다. BM-85 저장분에서는 첫 카드 쿠폰의 이중 기록이라 카드 값이 이긴다.
 *  - campaign.couponId·couponProvider(캠페인에 하나였던 연결 쿠폰)는 **쿠폰을 켠 카드
 *    전부**의 오브젝트에 합친다 — 옛 서버가 쿠폰을 켠 카드 전부에 그 쿠폰 링크를 실어
 *    보냈으므로(campaign.py _buildBrandMessageRecipient) 저장분의 의미가 같고, 한 카드에만
 *    옮기면 손대지 않은 캠페인이 로드 직후 나머지 카드의 링크 필수 에러에 걸린다(캠페인
 *    목록의 자동 활성화 게이트도 막힌다). 쿠폰을 켠 카드가 없으면 첫 카드다 — 연결만 있고
 *    유형이 없는 오브젝트가 되어 검증이 유형 선택을 요구한다(옛 "쿠폰을 연결했지만 쿠폰을
 *    넣은 카드가 없어요"와 같은 결과).
 *  - campaign.coupon 은 비운다. campaign.couponId·couponProvider 는 그대로 둔다 — 검증·
 *    폼·페이로드는 캐러셀에서 캠페인 레벨을 읽지 않고, 페이로드가 첫 카드 기준으로 다시 쓴다.
 * 카드 쿠폰에 couponId 가 하나라도 있으면 새 계약 저장분이라 카드는 손대지 않는다
 * (campaign.coupon 잔류값만 비운다 — 캐러셀은 캠페인 레벨 coupon 을 쓰지 않으므로 화면에
 * 없는 값을 폼에 남기지 않는다).
 * **로드 때 한 번만** 태운다 — 수동(CampaignEditor.convertCampaign)·자동(AutoCampaignEditor
 * 로드 effect)·캠페인 목록 게이트(MessageCampaign, 폼을 거치지 않은 레코드). 검증·
 * getLinkedCoupons 는 태우지 않는다: 폼에서 연결을 전부 풀면 카드에 couponId 가 없는데
 * 캠페인 레벨엔 이중 기록이 남아 있어, 다시 태우면 옛 저장분으로 오판해 되살아난다.
 * 바뀐 게 없으면 받은 객체를 그대로 돌려준다.
 */
export const hydrateBrandCoupon = (campaign = {}) => {
  if (campaign.messageType !== "uc") return campaign;
  const cards = campaign.cards;
  if (!Array.isArray(cards) || !cards.length) return campaign;
  const legacyCoupon = hasCouponInput(campaign.coupon, null)
    ? campaign.coupon
    : null;
  if (cards.some((card) => !isBlankValue(card?.coupon?.couponId))) {
    return legacyCoupon ? { ...campaign, coupon: null } : campaign;
  }
  const legacyLinked = !isBlankValue(campaign.couponId);
  if (!legacyCoupon && !legacyLinked) return campaign;
  const on = cards.map((card) => hasCouponInput(card?.coupon));
  const first = Math.max(on.indexOf(true), 0);
  const link = {
    couponId: campaign.couponId || null,
    couponProvider: campaign.couponProvider || null,
  };
  return {
    ...campaign,
    coupon: null,
    cards: cards.map((card, index) => {
      if (index === first) {
        return {
          ...(card || {}),
          coupon: { ...(legacyCoupon || {}), ...(card?.coupon || {}), ...link },
        };
      }
      if (on[index] && legacyLinked) {
        return { ...card, coupon: { ...card.coupon, ...link } };
      }
      return card;
    }),
  };
};

// ─────────────────────────────────────────────────────────────
// 말풍선 유형 (§5)
// ─────────────────────────────────────────────────────────────

/**
 * 유형별 규격. limits.newline 은 본문에 넣을 수 있는 줄바꿈 횟수다(서버 미검증이라
 * 프론트가 유일한 방어선 — 초과하면 카카오가 발송 시점에 거부하고 그 실패는 비동기다).
 * buttons.max 는 링크+채널추가 총 개수다. ut·ui 는 네이티브 쿠폰 ON 시 −1(BM-78, getButtonMax).
 * buttons.acPosition 은 AC 버튼이 놓일 자리 — 카카오 규격(KAPI 브랜드 템플릿 스펙, BM-82 확정):
 * "TEXT·IMAGE 는 첫번째 버튼, 그 외 메시지 타입은 마지막 버튼". ut·ui first / uw·ul·uc last.
 * 서버(daou.placeBrandMessageAcButton)가 wire 자리를 강제하므로 저장 순서로 거부되진 않지만,
 * 미리보기가 실제 렌더와 같아야 하므로 화면 순서도 이 값을 따른다.
 * coupon.descriptionMax 는 §5 쿠폰 표(uw·ul 18자 / ut·ui·uc 12자)이고, coupon 이
 * 없는 유형은 쿠폰을 못 쓴다는 뜻이다. 쿠폰 자리는 캐러셀만 cards[i].coupon(카드마다)
 * 이고 나머지는 campaign.coupon 이다 — "쿠폰 — 네이티브 오브젝트" 절.
 */
const ALL_MESSAGE_TYPES = [
  {
    key: "ut",
    label: "텍스트형",
    desc: "이미지 없이 글과 버튼만으로 보내요.",
    // 서버가 ut 를 지원한다(§6 BM_TYPE_UNSUPPORTED = "ut/ui/uw/ul/uc 가 아님").
    limits: { message: 1300, newline: 99 },
    image: null,
    coupon: { descriptionMax: 12 },
    buttons: {
      max: 5,
      nameMax: 14,
      allowChannelAdd: true,
      acPosition: "first",
    },
  },
  {
    key: "ui",
    // BM-26 (A)안: PM "와이드 이미지형"의 수치(1,300자·버튼 5개·14자)가 서버 ui 와 일치한다.
    label: "와이드 이미지형",
    desc: "사진 한 장과 본문으로 보내요.",
    limits: { message: 1300, newline: 99 },
    image: { required: true, kind: "default", count: 1 },
    coupon: { descriptionMax: 12 },
    buttons: {
      max: 5,
      nameMax: 14,
      allowChannelAdd: true,
      acPosition: "first",
    },
  },
  {
    key: "ul",
    label: "와이드 리스트형",
    // BM-37: 총 3~5개(대표 1 + 서브 2~4).
    desc: "대표 항목 1개와 항목 2~4개를 목록으로 보여줘요.",
    // ul 은 message 를 쓰지 않는다. title 이 리스트 헤더로 그대로 실린다.
    limits: { title: 20 },
    listItems: {
      min: 3,
      max: 5,
      firstTitleMax: 25,
      titleMax: 30,
      titleNewline: 1,
      imageRequired: true,
      // §5: 메인 타이틀 25자는 **선택**, 서브 타이틀 30자는 필수.
      firstTitleRequired: false,
      titleRequired: true,
      // 링크는 Mobile 필수 / PC 선택.
      linkMoRequired: true,
    },
    coupon: { descriptionMax: 18 },
    buttons: {
      max: 2,
      nameMax: 8,
      allowChannelAdd: true,
      acPosition: "last",
    },
  },
  {
    key: "uc",
    label: "캐러셀 피드형",
    desc: "카드 2~6장을 옆으로 넘겨 보여줘요.",
    cards: {
      min: 2,
      max: 6,
      headerMax: 20,
      messageMax: 180,
      messageNewline: 10,
      imageRequired: true,
      // BM-28: "모든 카드 비율 동일" 제약은 문서에 근거가 없다(캐러셀커머스 ua 전용).
      // 서버도 검증하지 않는다 → sameRatio 플래그를 두지 않는다.
      buttons: {
        // BM-40: 카드당 1~2개 필수(서버 BM_CARD_BUTTON_REQUIRED).
        min: 1,
        max: 2,
        nameMax: 8,
        allowChannelAdd: true,
        acPosition: "last",
      },
    },
    // 쿠폰은 cards[i].coupon 으로 카드마다 따로 둔다(carousel.list[i].attachment.coupon).
    // 연결 쿠폰도 그 안에 카드별(cards[i].coupon.couponId, BM-86).
    coupon: { descriptionMax: 12 },
    // 카드 하단 "더보기" 버튼. {linkMo, linkPc}
    tail: true,
    // 캐러셀은 카드 버튼만 쓴다. 캠페인 레벨 buttons 는 비워둘 것.
    buttons: null,
  },
  {
    key: "uw",
    label: "와이드 이미지형(구)",
    desc: "짧은 문구와 이미지 1장. 화면에서는 쓰지 않아요.",
    // BM-26: PM 규격에 없는 유형이라 선택 목록에서 제외한다. 서버는 계속 받는다.
    hidden: true,
    // §5 표 기준 76자·줄바꿈 5회(BM-26 표는 1회로 적혀 있으나 미노출이라 영향 없음).
    limits: { message: 76, newline: 5 },
    image: { required: true, kind: "wide", count: 1 },
    coupon: { descriptionMax: 18 },
    buttons: {
      max: 2,
      nameMax: 8,
      allowChannelAdd: true,
      acPosition: "last",
    },
  },
];

/** 화면 선택 목록 — uw 만 제외(PM 규격에 없음). 현재 ut·ui·ul·uc 4종. */
export const MESSAGE_TYPES = ALL_MESSAGE_TYPES.filter((type) => !type.hidden);

/** 저장 페이로드에서 허용하는 messageType 전체(hidden 포함). */
const MESSAGE_TYPE_KEYS = ALL_MESSAGE_TYPES.map((type) => type.key);

export const MESSAGE_TYPE_BY_KEY = ALL_MESSAGE_TYPES.reduce((obj, type) => {
  obj[type.key] = type;
  return obj;
}, {});

export const getMessageType = (messageType) =>
  MESSAGE_TYPE_BY_KEY[messageType] || null;

/**
 * 지금 발송 가능한 유형인가. hidden(uw)은 false.
 * serverPending 은 서버가 아직 못 받는 유형을 임시로 잠그는 장치다 — 현재 해당 없음.
 */
export const isMessageTypeSendable = (messageType) => {
  const type = getMessageType(messageType);
  return !!type && !type.hidden && !type.serverPending;
};

/** 이 유형이 쿠폰을 쓸 수 있는가(type.coupon 유무). */
export const canUseCoupon = (messageType) =>
  !!getMessageType(messageType)?.coupon;

/** 유형별 쿠폰 설명 상한(§5). 쿠폰을 못 쓰는 유형은 null. */
export const getCouponDescriptionMax = (messageType) =>
  getMessageType(messageType)?.coupon?.descriptionMax ?? null;

// ─────────────────────────────────────────────────────────────
// 발송 대상
// ─────────────────────────────────────────────────────────────

/**
 * sendTarget 계약(daou.BRANDMESSAGE_MARKETING_TARGETINGS).
 * friend 는 I형(채널 친구)이라 승인이 필요 없고, M/N/O 는 브랜드메시지 승인과
 * 080 무료수신거부 번호가 모두 있어야 발송된다.
 */
export const SEND_TARGETS = [
  {
    key: "friend",
    label: "채널 친구에게 발송",
    desc: "마케팅 수신에 동의하고, 우리 채널을 친구 추가한 고객",
    needsApproval: false,
    cost: `${FRIEND_COST_RANGE[0]}-${FRIEND_COST_RANGE[1]}캔디/건`,
  },
  {
    key: "N",
    label: "채널 친구가 아닌 고객에게 발송",
    desc: "마케팅 수신에 동의했지만 아직 친구가 아닌 고객",
    needsApproval: true,
    cost: `${CREDIT_COST.marketing}캔디/건`,
  },
  {
    key: "M",
    label: "마케팅 동의 고객 전체에게 발송",
    desc: "친구 여부와 상관없이 마케팅 수신에 동의한 모든 고객",
    needsApproval: true,
    cost: `${CREDIT_COST.marketing}캔디/건`,
  },
  {
    key: "O",
    // 서버 계약에는 있으나 대상이 friend 와 겹치는데 단가만 비싸다(4→5캔디).
    // 시안에도 없으므로 노출 여부는 호출부가 판단한다(BM-20).
    label: "채널 친구에게 발송 (마케팅)",
    desc: "마케팅 수신에 동의한 고객 중 우리 채널을 친구 추가한 고객",
    needsApproval: true,
    cost: `${CREDIT_COST.marketing}캔디/건`,
    hidden: true,
  },
];

export const isMarketingTarget = (sendTarget) =>
  MARKETING_TARGETS.includes(sendTarget);

/** 건당 캔디. Utils.getCreditCost 가 이 함수를 통해 브랜드 단가를 읽는다. */
/**
 * 건당 캔디 단가. messageType 은 브랜드 전환 시 항상 기본값(ui)이 들어가고
 * 검증도 필수라 실사용에서 비지 않는다 — 폴백은 옛 저장분·손상 데이터용 방어값이며,
 * 낮게 잡아 잔액 부족으로 발송이 막히는 쪽보다 최대값이 안전하다.
 */
export const getBrandMessageCost = (sendTarget, messageType) => {
  if (isMarketingTarget(sendTarget)) return CREDIT_COST.marketing;
  return CREDIT_COST.friend[messageType] ?? FRIEND_COST_RANGE[1];
};

/** 채널 추가(AC) 버튼은 M·N 에서만 쓸 수 있다. friend·O 는 이미 채널 친구다. */
export const canUseChannelAddButton = (sendTarget) =>
  ["M", "N"].includes(sendTarget);

/**
 * 말풍선 하단 수신거부 문구(표시 전용).
 * 마케팅 대상인데 080 번호가 없으면 null — 호출부가 발송을 막아야 한다.
 */
/** 친구(I형) 메시지 하단 고정 문구 — 카카오가 붙이는 원문 그대로(노션 스펙 표). */
export const FRIEND_UNSUBSCRIBE_TEXT = "수신거부 | 홈 > 채널차단";

export const getUnsubscribeText = (sendTarget, unsubscribePhoneNumber) => {
  if (!isMarketingTarget(sendTarget)) {
    return FRIEND_UNSUBSCRIBE_TEXT;
  }
  return unsubscribePhoneNumber
    ? `무료수신거부 ${unsubscribePhoneNumber}`
    : null;
};

/**
 * 미리보기 전용 광고 표기. 서버가 발송 직전에 본문 앞에 붙이므로
 * (daou.applyBrandMessageAdMarker) 저장 페이로드에는 절대 포함하지 않는다.
 */
export const getAdPreviewText = (channelName) =>
  channelName ? `(광고) ${channelName}` : "(광고)";

// ─────────────────────────────────────────────────────────────
// AC(채널추가) 버튼 배치 규칙 (BM-41)
// ─────────────────────────────────────────────────────────────

const buttonType = (button) => String(button?.type || "").toUpperCase();

const isChannelAddButton = (button) => buttonType(button) === "AC";

/**
 * AC 버튼이 놓여야 할 자리. 세로형(ut·ui)은 "first", 가로형(ul·uc)은 "last".
 * uc 는 카드 버튼 규칙을 그대로 쓴다(캠페인 레벨 버튼이 없다).
 */
export const getChannelAddPosition = (messageType) => {
  const type = getMessageType(messageType);
  if (!type) return null;
  return type.buttons?.acPosition ?? type.cards?.buttons?.acPosition ?? null;
};

/**
 * AC 버튼을 유형 규칙대로 앞/뒤로 옮긴 새 배열. 나머지 버튼의 상대 순서는 유지한다.
 * 저장 직전(buildBrandMessagePayload)과 미리보기가 같은 순서를 쓰게 하는 순수 함수다.
 */
export const orderButtonsForType = (buttons, messageType) => {
  const list = (buttons || []).filter(Boolean);
  const position = getChannelAddPosition(messageType);
  if (!position) return list;
  const channelAdd = list.filter(isChannelAddButton);
  if (!channelAdd.length) return list;
  const rest = list.filter((button) => !isChannelAddButton(button));
  return position === "first"
    ? [...channelAdd, ...rest]
    : [...rest, ...channelAdd];
};

// ─────────────────────────────────────────────────────────────
// 버튼 상한
// ─────────────────────────────────────────────────────────────

/** 쿠폰이 버튼 한 칸을 먹는 유형 — 카카오 원문 "버튼 최대 5개, 쿠폰 삽입 시 4개"(BM-78). */
const COUPON_TAKES_BUTTON_SLOT = ["ut", "ui"];

/**
 * 캠페인 버튼 배열의 **실효 상한**. 서버(_validateBrandMessageCampaign)와 같은 규칙:
 * ut·ui 에서 네이티브 쿠폰(coupon 또는 couponId)이 켜져 있으면 buttons.max − 1.
 * 화면 상한(linkMax)·검증(validateButtons)이 모두 이 값을 써야 서버 거절과 어긋나지 않는다.
 * 버튼을 못 쓰는 유형이면 0.
 */
export const getButtonMax = (messageType, { couponOn = false } = {}) => {
  const type = getMessageType(messageType);
  const rule = type?.buttons;
  if (!rule) return 0;
  const takesSlot =
    couponOn && !!type.coupon && COUPON_TAKES_BUTTON_SLOT.includes(type.key);
  return Math.max(0, rule.max - (takesSlot ? 1 : 0));
};

// ─────────────────────────────────────────────────────────────
// 글자수 / 줄바꿈
// ─────────────────────────────────────────────────────────────

/** \r\n·\r·\n 을 한 번으로 세는 줄바꿈 카운터. */
export const countNewlines = (value) =>
  value ? (String(value).match(/\r\n|\r|\n/g) || []).length : 0;

/** 유형별 글자수·줄바꿈·개수 상한을 한 객체로 평탄화해 돌려준다. 미지원 유형은 null. */
export const getMessageLimit = (messageType) => {
  const type = getMessageType(messageType);
  if (!type) return null;
  return {
    message: type.limits?.message ?? null,
    messageNewline: type.limits?.newline ?? null,
    title: type.limits?.title ?? null,
    firstItemTitle: type.listItems?.firstTitleMax ?? null,
    itemTitle: type.listItems?.titleMax ?? null,
    itemTitleNewline: type.listItems?.titleNewline ?? null,
    items: type.listItems
      ? { min: type.listItems.min, max: type.listItems.max }
      : null,
    cardHeader: type.cards?.headerMax ?? null,
    cardMessage: type.cards?.messageMax ?? null,
    cardMessageNewline: type.cards?.messageNewline ?? null,
    cards: type.cards ? { min: type.cards.min, max: type.cards.max } : null,
    buttons: type.buttons || null,
    cardButtons: type.cards?.buttons || null,
    // 쿠폰은 별도 오브젝트지만 ut·ui 에서는 버튼 한 칸을 먹는다(BM-78 → getButtonMax)
    coupon: type.coupon || null,
    couponDescription: type.coupon?.descriptionMax ?? null,
    imageLink: LINK_MAX_LENGTH,
  };
};

/**
 * (광고) 프리픽스가 먹는 길이. 서버가 발송 직전 `"(광고) {채널명}\n"` 을 본문 앞에
 * 붙이므로(daou.applyBrandMessageAdMarker) 사용자가 쓸 수 있는 본문은 그만큼 짧다.
 * 대상(friend/M/N/O)과 무관하게 항상 붙는다.
 */
const getAdPrefixLength = (channelName) =>
  getAdPreviewText(channelName).length + 1; // +1 = 프리픽스 뒤 줄바꿈

/**
 * 본문 카운터가 써야 하는 **실효 상한**. 서버 검증은 원본 본문만 세지만
 * 실제 발송물은 프리픽스가 붙은 길이라, 상한에 딱 맞춰 쓰면 카카오가 발송 시점에
 * 자르거나 거부할 수 있다(그 실패는 비동기라 화면에 뜨지 않는다).
 * `#{CUSTOMER_NAME}` 치환도 발송 시 길이를 늘리므로 여유는 더 필요하다.
 * 본문이 없는 유형(ul·uc)은 null.
 */
export const getEffectiveMessageLimit = (messageType, channelName) => {
  const max = getMessageType(messageType)?.limits?.message;
  if (max == null) return null;
  return Math.max(0, max - getAdPrefixLength(channelName));
};

/** 줄바꿈도 프리픽스가 1회를 먹는다. 본문이 없는 유형은 null. */
export const getEffectiveNewlineLimit = (messageType) => {
  const max = getMessageType(messageType)?.limits?.newline;
  if (max == null) return null;
  return Math.max(0, max - 1);
};

// ─────────────────────────────────────────────────────────────
// 이미지 규격
// ─────────────────────────────────────────────────────────────

const buildImageSpec = (kind, ratio, ratioText, size) => ({
  kind,
  // [min, max] 비율 구간 배열(가로/세로).
  ratio,
  ratioText,
  width: size ? size[0] : null,
  height: size ? size[1] : null,
  minWidth: IMAGE_MIN_WIDTH,
  maxBytes: IMAGE_MAX_BYTES,
  formats: IMAGE_FORMATS,
  mimeTypes: IMAGE_MIME_TYPES,
});

/**
 * LS_validateImage(options.kind) 에 그대로 넘기는 키.
 * BM-28: 비즈뿌리오 원문 "0.5 ≤ 세로/가로 ≤ 1.333" → 가로/세로 0.75~2.0.
 *   서버 BRANDMESSAGE_IMAGE_RATIO 와 같은 값이다.
 * 리스트형만 고정 비율(대표 2:1 / 서브 1:1)이다.
 */
const IMAGE_SPEC_BY_KIND = {
  default: buildImageSpec("default", [[0.75, 2.0]], "2:1 ~ 3:4", [800, 400]),
  wide: buildImageSpec("wide", [[0.75, 2.0]], "2:1 ~ 3:4", [800, 400]),
  wideItemListFirst: buildImageSpec(
    "wideItemListFirst",
    [[1.95, 2.05]],
    "2:1",
    [800, 400],
  ),
  // 서브 항목은 권장 픽셀 없이 비율만 규정돼 있다.
  wideItemList: buildImageSpec("wideItemList", [[0.95, 1.05]], "1:1"),
  carouselFeed: buildImageSpec(
    "carouselFeed",
    [[0.75, 2.0]],
    "2:1 ~ 3:4",
    [800, 400],
  ),
};

// ut 는 이미지가 없어 kind 가 없다.
const IMAGE_KIND_BY_TYPE = { ui: "default", uw: "wide", uc: "carouselFeed" };

/**
 * 이미지 슬롯 kind. ul 만 인덱스로 갈린다(0=대표 2:1, 1~=항목 1:1).
 * LS_validateImage(key, "brandmessage", level, { kind }) 의 kind 로 그대로 쓴다.
 */
export const getImageKind = (messageType, index = 0) => {
  if (messageType === "ul") {
    return index === 0 ? "wideItemListFirst" : "wideItemList";
  }
  return IMAGE_KIND_BY_TYPE[messageType] || null;
};

export const getImageSpecByKind = (kind) => IMAGE_SPEC_BY_KIND[kind] || null;

export const getImageSpec = (messageType, index = 0) =>
  getImageSpecByKind(getImageKind(messageType, index));

/**
 * 업로드 전 로컬 비율 검사. 서버 _validateBrandMessageImage 와 같은 판정이라
 * LS_validateImage 왕복 없이 먼저 걸러낼 수 있다.
 */
export const isImageRatioValid = (width, height, kind) => {
  const spec = getImageSpecByKind(kind);
  if (!spec || !width || !height) return false;
  if (width < spec.minWidth) return false;
  const ratio = width / height;
  return spec.ratio.some(([min, max]) => ratio >= min && ratio <= max);
};

// ─────────────────────────────────────────────────────────────
// 발송 가능 시간 (KST)
// ─────────────────────────────────────────────────────────────

const KST_OFFSET_MINUTES = 9 * 60;
const NAIVE_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})/;
const TIME_ONLY_RE = /^(\d{1,2}):(\d{2})$/;
const HAS_ZONE_RE = /(Z|[+-]\d{2}:?\d{2})$/;

const toMinutes = (hhmm) => {
  const [hour, minute] = String(hhmm).split(":").map(Number);
  return hour * 60 + minute;
};

/**
 * 값 → KST 벽시계 {hour, minute, timestamp}.
 *
 * 타임존 없는 문자열("2026-09-01T10:00")은 서버가 KST 로 해석하므로 여기서도
 * 브라우저 로컬 타임존을 태우지 않고 벽시계 그대로 읽는다. Date 객체나 타임존이
 * 붙은 문자열만 절대시각 → KST 로 환산한다.
 * "HH:mm"(자동 캠페인 sendAtTime)은 날짜가 없어 timestamp 가 null 이다.
 */
const toKstWallClock = (value) => {
  if (typeof value === "string") {
    const timeOnly = value.trim().match(TIME_ONLY_RE);
    if (timeOnly) {
      return {
        hour: Number(timeOnly[1]),
        minute: Number(timeOnly[2]),
        timestamp: null,
      };
    }
    const naive = value.trim().match(NAIVE_DATETIME_RE);
    if (naive && !HAS_ZONE_RE.test(value.trim())) {
      const [, year, month, day, hour, minute] = naive.map(Number);
      return {
        hour,
        minute,
        timestamp:
          Date.UTC(year, month - 1, day, hour, minute) -
          KST_OFFSET_MINUTES * 60000,
      };
    }
  }
  const date =
    value instanceof Date ? value : value == null ? new Date() : new Date(value);
  if (isNaN(date.getTime())) return null;
  const shifted = new Date(date.getTime() + KST_OFFSET_MINUTES * 60000);
  return {
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    timestamp: date.getTime(),
  };
};

/**
 * 발송 가능 시간대인지. 경계 포함(서버도 start <= t <= end).
 * date 생략 시 지금. auto=true 는 자동 캠페인 발송창(마감 1시간 빠름).
 */
export const isSendableTime = (date = new Date(), { auto = false } = {}) => {
  const clock = toKstWallClock(date);
  if (!clock) return false;
  const window = auto ? SEND_WINDOW.auto : SEND_WINDOW.manual;
  const minutes = clock.hour * 60 + clock.minute;
  return minutes >= toMinutes(window.start) && minutes <= toMinutes(window.end);
};

const formatKoreanTime = (hhmm) => {
  const [hour, minute] = String(hhmm).split(":").map(Number);
  const meridiem = hour < 12 ? "오전" : "오후";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return minute
    ? `${meridiem} ${hour12}시 ${minute}분`
    : `${meridiem} ${hour12}시`;
};

/** "오전 8시 ~ 오후 8시 50분" */
export const getSendWindowText = ({ auto = false } = {}) => {
  const window = auto ? SEND_WINDOW.auto : SEND_WINDOW.manual;
  return `${formatKoreanTime(window.start)} ~ ${formatKoreanTime(window.end)}`;
};

export const getSendWindowMessage = ({ auto = false } = {}) =>
  `${auto ? "자동 브랜드 메시지는" : "브랜드 메시지는"} ${getSendWindowText({
    auto,
  })} 사이에만 보낼 수 있어요.`;

/** 예약 가능 여부 — 발송창 + 지금부터 30일 이내. */
const isReservableDate = (date, { auto = false, now } = {}) => {
  if (!isSendableTime(date, { auto })) return false;
  const clock = toKstWallClock(date);
  if (!clock || clock.timestamp == null) return false;
  const base = now ? new Date(now).getTime() : Date.now();
  return clock.timestamp <= base + RESERVE_MAX_DAYS * 24 * 60 * 60 * 1000;
};

// ─────────────────────────────────────────────────────────────
// 발송 전 검증 (§6-1 차단 ✅ 항목 미러)
// ─────────────────────────────────────────────────────────────

const length = (value) => (value ? String(value).length : 0);

/** 서버 _hasImage 와 같은 판정 — 빈 맵 {} 은 이미지가 아니다. */
const hasImage = (image) => !!(image?.key || image?.url);

/**
 * 버튼·쿠폰·이미지·더보기 링크의 형식 검사. 스킴만 있는 "https://" 는 카카오가 발송
 * 시점에 거부하고(그 실패는 비동기라 화면에 안 뜬다) 테스트 발송도 나가지 않는다 —
 * 그래서 스킴 뒤에 **호스트가 실제로 있는 URL** 만 통과시킨다.
 *  - http/https 만, 공백 없음
 *  - 호스트에 점이 하나 이상(예: thegrowing.co) 또는 localhost, 포트·경로·쿼리·해시 선택
 *  - 한글 도메인 등 유니코드 호스트는 URL 파서가 punycode 로 정규화하므로 통과한다
 */
export const isHttpLink = (url) => {
  if (typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname;
    if (!host) return false;
    if (host === "localhost" || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host)) return true;
    // 라벨.라벨 형태 — 마지막 라벨(TLD)은 글자 2자 이상, 라벨은 하이픈 시작/끝 불가
    return /^(?:[a-z0-9\u00a1-\uffff](?:[a-z0-9\u00a1-\uffff-]{0,61}[a-z0-9\u00a1-\uffff])?\.)+(?:[a-z\u00a1-\uffff]{2,}|xn--[a-z0-9-]{2,})\.?$/i.test(
      host,
    );
  } catch (e) {
    return false;
  }
};

/**
 * 웹 링크(이미지 클릭 링크·리스트 항목 링크·더보기 링크·버튼 PC 링크) 검사.
 * label 은 항상 "…링크" 로 끝나 조사가 를/는 으로 고정된다.
 * 버튼의 **모바일** 링크에는 쓰지 않는다 — AL(앱링크)은 http 가 아닌 스킴을 쓴다.
 * PC 링크는 앱 스킴을 열 수 없어 버튼 유형과 무관하게 http(s) 다.
 */
const validateWebLink = (
  errors,
  url,
  { field, label, code, required = false },
) => {
  if (!url) {
    if (required) {
      errors.push({ field, code, message: `${label}를 입력해 주세요.` });
    }
    return;
  }
  if (length(url) > LINK_MAX_LENGTH) {
    errors.push({
      field,
      code,
      message: `${label}는 ${LINK_MAX_LENGTH}자까지 넣을 수 있어요.`,
    });
    return;
  }
  if (!isHttpLink(url)) {
    errors.push({
      field,
      code,
      message: `${label}는 https:// 로 시작하는 올바른 주소여야 해요. 예: https://example.com`,
    });
  }
};

/**
 * 쿠폰 값 규격(§5). shipping 은 값이 필요 없고, 숫자형은 정수 범위, free·up 은
 * 상품명 길이다. 화면이 입력 중 필드 단위 검사에 그대로 쓸 수 있게 내보낸다.
 */
export const isCouponValueValid = (couponType, value) => {
  const type = getCouponType(couponType);
  if (!type) return false;
  const rule = type.value;
  if (rule.kind === "none") return true;
  if (isBlankValue(value)) return false;
  const text = String(value).trim();
  if (rule.kind === "number") {
    // 화면이 천 단위 구분자를 넣어 줄 수 있어 콤마는 벗겨 낸다.
    // 서버는 str(value).isdigit() 로 보므로 "3e3"·"3.0" 처럼 Number 로는 정수가
    // 되는 표기도 막아야 판정이 같아진다.
    const digits = text.replace(/,/g, "");
    if (!/^\d+$/.test(digits)) return false;
    const number = Number(digits);
    return number >= rule.min && number <= rule.max;
  }
  return length(text) <= rule.maxLength && countNewlines(text) === 0;
};

/** 자사몰 쿠폰 provider — 서버 PROVIDER_COUPON_PROVIDERS(campaign.py:903)와 같은 목록. */
const PROVIDER_COUPON_PROVIDERS = ["cafe24", "imweb"];

/** 자사몰 쿠폰인가 — 화면이 링크 입력칸·예약 가능 여부를 정할 때 쓴다(BM-73). */
export const isProviderCouponProvider = (provider) =>
  PROVIDER_COUPON_PROVIDERS.includes(provider);

/**
 * 캠페인의 연결 쿠폰 목록 [{couponId, couponProvider, field, index}].
 * 본문형·ul 은 campaign.couponId 하나(field "coupon.couponId"), 캐러셀(uc)은
 * cards[i].coupon.couponId 카드마다(field "cards[i].coupon.couponId", index 1-based).
 * 캐러셀에서 campaign.couponId 는 전환기 이중 기록(첫 카드 값)이라 읽지 않는다 — 폼
 * 편집 중에는 페이로드를 만들기 전이라 카드와 어긋나 있다. 옛 저장분(폼을 거치지 않은
 * 목록 레코드)은 호출부가 hydrateBrandCoupon 을 먼저 태운다 — 여기서 태우면 연결을 전부
 * 푼 폼의 잔류 이중 기록이 되살아난다(hydrateBrandCoupon 주석).
 */
export const getLinkedCoupons = (campaign = {}) => {
  if (campaign.messageType !== "uc") {
    return isBlankValue(campaign.couponId)
      ? []
      : [
          {
            couponId: campaign.couponId,
            couponProvider: campaign.couponProvider || null,
            field: "coupon.couponId",
            index: null,
          },
        ];
  }
  return (campaign.cards || []).reduce((list, card, index) => {
    const couponId = card?.coupon?.couponId;
    if (isBlankValue(couponId)) return list;
    list.push({
      couponId,
      couponProvider: card.coupon.couponProvider || null,
      field: `cards[${index}].coupon.couponId`,
      index: index + 1,
    });
    return list;
  }, []);
};

/**
 * 자사몰 쿠폰이 연결돼 있는가 — 예약 잠금·즉시발송 강제(BM-73) 판정.
 * 캐러셀은 **카드 중 하나라도** 자사몰이면 참이다(서버 매핑 필터·예약 잠금도 같은 기준,
 * BM-86 ④). 판정은 캠페인 필드(couponProvider) 우선 — 서버 isProviderCoupon 과 같다 —
 * 이고, 자사몰 쿠폰 맵에 레코드가 있으면 provider 값이 비어 있어도 자사몰로 본다.
 */
export const hasProviderCouponLinked = (campaign, providerCouponsById) =>
  getLinkedCoupons(campaign).some(
    ({ couponId, couponProvider }) =>
      isProviderCouponProvider(couponProvider) ||
      !!providerCouponsById?.[couponId],
  );

/**
 * 연결 쿠폰(couponId) 검사 — 서버 _validateBrandMessageCoupon(campaign.py:224-238)과
 * **같은 순서·같은 에러키**다. linkMo 를 직접 넣었으면 서버도 이 블록을 건너뛴다.
 *
 *   1. couponId 없음        → BM_COUPON_LINK_REQUIRED
 *   2. 자사몰 쿠폰           → 통과(링크는 사용자가 직접 넣는다, BM-73)
 *   3. 쿠폰 레코드 없음(삭제) → BM_COUPON_NOT_FOUND
 *   4. 바코드 쿠폰이 아님     → BM_COUPON_LINK_REQUIRED
 *
 * 4 가 1 과 같은 키인 건 서버가 그렇게 던지기 때문이다(§6-1) — 수신자별 쿠폰 링크는
 * 자체 **바코드** 쿠폰으로만 만들 수 있어서, 못 만들면 결국 "링크가 없다"와 같은 상태다.
 *
 * 3·4 는 쿠폰 레코드가 있어야 판정되므로 `couponsById` 주입에 달려 있다.
 * **주입이 없거나 아직 비어 있으면(조회 전) 3·4 를 건너뛴다** — 안 넘긴 호출부에서
 * 멀쩡한 쿠폰을 "없는 쿠폰"으로 오탐하면 보낼 수 있는 캠페인을 프론트가 막게 된다.
 * 2 는 캠페인 필드(couponProvider)만으로도 판정되므로 주입과 무관하게 항상 돈다 —
 * 서버 isProviderCoupon(campaign.py:911)도 레코드가 아니라 이 필드를 본다.
 *
 * gotcha: 화면이 넘기는 맵은 대개 getBarcodeCouponsById(method "barcode" 만) 기반이라
 * **비바코드 자체 쿠폰은 맵에 아예 없어 4 가 아니라 3(NOT_FOUND)으로 잡힌다.** 둘 다
 * 발송을 막으니 안전한 방향이고 문구만 달라진다. 정확히 가르려면 비바코드까지 담긴
 * 맵을 넘기면 된다.
 */
const validateLinkedCoupon = (
  push,
  { couponId, couponProvider, couponsById, label, index },
) => {
  if (isBlankValue(couponId)) {
    push(
      ".linkMo",
      "BM_COUPON_LINK_REQUIRED",
      `${label}을 눌렀을 때 열릴 곳이 없어요. 보낼 쿠폰을 연결하거나 쿠폰 링크를 넣어 주세요.`,
    );
    return;
  }

  // 아래 3키는 문제의 주인이 링크 입력칸이 아니라 "연결한 쿠폰"이라 field 를 나눈다.
  // 호출부의 섹션 매핑은 /coupon/i 라 두 경로 모두 쿠폰 영역으로 떨어진다.
  // 캐러셀은 카드마다 연결 쿠폰이 달라(BM-86) 어느 카드인지 문구에 싣는다.
  const where = index == null ? "" : `${index}번 카드에 `;
  const linked = couponsById?.[couponId];
  // 자사몰 쿠폰은 서버가 발급까지 하지만 **수신자별 링크는 못 만든다**(BM-73) —
  // 링크가 비어 있으면 서버가 1단계에서 BM_COUPON_LINK_REQUIRED 로 거절한다.
  // 화면은 이때 링크 입력칸을 열어 두므로(BrandCouponFields autoLink=false)
  // 여기서는 "링크를 채웠는가"만 본다.
  if (
    PROVIDER_COUPON_PROVIDERS.includes(couponProvider) ||
    PROVIDER_COUPON_PROVIDERS.includes(linked?.provider)
  ) {
    return;
  }

  // 미주입·조회 전 맵은 "없음"이 아니라 "미확인"으로 다룬다(승인·080 과 같은 원칙).
  if (!couponsById || !Object.keys(couponsById).length) return;

  if (!linked) {
    push(
      ".couponId",
      "BM_COUPON_NOT_FOUND",
      `${where}연결한 쿠폰을 찾을 수 없어요. 삭제됐을 수 있으니 쿠폰을 다시 연결해 주세요.`,
    );
    return;
  }
  if (linked.method !== "barcode") {
    push(
      ".couponId",
      "BM_COUPON_LINK_REQUIRED",
      `${where}연결한 쿠폰이 바코드 쿠폰이 아니라 고객마다 다른 쿠폰 링크를 만들 수 없어요. 바코드 쿠폰을 연결하거나 쿠폰 링크를 넣어 주세요.`,
    );
  }
};

/**
 * 쿠폰 오브젝트 검사 — §6-1 의 쿠폰 4키를 그대로 쓴다.
 * field 는 캠페인이면 "coupon.*", 캐러셀이면 "cards[i].coupon.*" 다.
 * index(1-based)는 서버가 `KEY:N` 으로 주는 위치와 같은 의미라, 화면이 필요하면
 * `${code}:${index}` 로 되붙일 수 있다(§6). code 자체에는 붙이지 않는다 —
 * 이 모듈의 다른 카드 에러(BM_CARD_*)와 매칭 규칙을 맞추기 위해서다.
 *
 * linkMo 는 §5 상 필수지만 couponId(연결 쿠폰)가 있으면 서버가 수신자별 쿠폰 링크로
 * 채우므로 그때는 입력을 요구하지 않는다.
 */
const validateCoupon = (
  errors,
  coupon,
  {
    messageType,
    couponId,
    couponProvider,
    couponsById,
    field = "coupon",
    index,
    label = "쿠폰",
  } = {},
) => {
  // 연결 쿠폰(couponId)만 있고 오브젝트가 비어도 토글은 ON 이라 검증 대상이다(BM-49).
  if (!hasCouponInput(coupon, couponId)) return;
  const push = (path, code, message) =>
    errors.push({
      field: `${field}${path}`,
      code,
      message,
      ...(index == null ? {} : { index }),
    });

  if (!canUseCoupon(messageType)) {
    push(
      "",
      "BM_COUPON_TYPE_INVALID",
      "이 메시지 유형에는 쿠폰을 넣을 수 없어요.",
    );
    return;
  }

  // coupon 은 null 일 수 있다(couponId 만 있는 상태) — 이 아래 필드 접근은 전부
  // `if (!type) return` 뒤라 안전하다.
  const type = getCouponType(coupon?.type);
  if (!type) {
    // 유형을 모르면 값 규격을 판정할 수 없다 — 유형부터 고르게 한다.
    push(".type", "BM_COUPON_TYPE_INVALID", `${label} 유형을 선택해 주세요.`);
    return;
  }

  const rule = type.value;
  if (rule.kind !== "none" && !isCouponValueValid(type.key, coupon.value)) {
    push(
      ".value",
      "BM_COUPON_VALUE_INVALID",
      rule.kind === "number"
        ? `${label} ${type.valueLabel}은 ${rule.min.toLocaleString()}~${rule.max.toLocaleString()}${rule.unit} 사이 숫자로 넣어 주세요.`
        : `${label} ${type.valueLabel}은 ${rule.maxLength}자까지 넣을 수 있어요.`,
    );
  }

  const descriptionMax = getCouponDescriptionMax(messageType);
  if (coupon.description) {
    if (countNewlines(coupon.description)) {
      push(
        ".description",
        "BM_COUPON_DESCRIPTION_INVALID",
        `${label} 설명에는 줄바꿈을 넣을 수 없어요.`,
      );
    } else if (
      descriptionMax != null &&
      length(coupon.description) > descriptionMax
    ) {
      push(
        ".description",
        "BM_COUPON_DESCRIPTION_INVALID",
        `${label} 설명은 ${descriptionMax}자까지 넣을 수 있어요.`,
      );
    }
  }

  // linkMo 를 직접 넣었으면 연결 쿠폰은 발급 실패 시 폴백일 뿐이라 서버가 안 본다.
  if (!coupon.linkMo) {
    validateLinkedCoupon(push, {
      couponId,
      couponProvider,
      couponsById,
      label,
      index,
    });
  }
  // 빈 값이면 no-op 이라 위 분기와 중복되지 않는다.
  validateWebLink(errors, coupon.linkMo, {
    field: `${field}.linkMo`,
    label: `${label} 모바일 링크`,
    code: "BM_COUPON_LINK_INVALID",
  });
  validateWebLink(errors, coupon.linkPc, {
    field: `${field}.linkPc`,
    label: `${label} PC 링크`,
    code: "BM_COUPON_LINK_INVALID",
  });
};

/**
 * BM-29 이전에 저장된 쿠폰 버튼. 지금은 campaign.coupon 오브젝트가 쿠폰을 담당하므로
 * 검증에서도 페이로드에서도 배열에서 빼고 센다(둘이 어긋나면 저장 후 개수가 달라진다).
 */
const isLegacyCouponButton = (button) => buttonType(button) === "COUPON";

/**
 * 버튼 배열 검사.
 * rule.max 는 링크+채널추가 총량이다 — 쿠폰은 버튼이 아니라 여기에 안 들어간다(§5).
 * AC 는 개수 1개 + 규칙 위치(first|last)까지 본다(BM-41 ②).
 */
const validateButtons = (
  errors,
  buttons,
  rule,
  { sendTarget, field, label },
) => {
  // 인덱스는 이 목록 기준이다 — normalizeButtons 도 같은 필터를 쓰므로
  // 화면 필드 경로(buttons[i])와 저장 순서가 어긋나지 않는다.
  const list = (buttons || []).filter(
    (button) => button && !isLegacyCouponButton(button),
  );
  if (!rule) {
    if (list.length) {
      errors.push({
        field,
        code: "BM_BUTTON_LIMIT",
        message: `${label}은 사용할 수 없어요.`,
      });
    }
    return;
  }
  if (list.length > rule.max) {
    errors.push({
      field,
      code: "BM_BUTTON_LIMIT",
      message: `${label}은 최대 ${rule.max}개까지 넣을 수 있어요.`,
    });
  }
  if (rule.min && list.length < rule.min) {
    errors.push({
      field,
      code: "BM_CARD_BUTTON_REQUIRED",
      message: `${label}을 최소 ${rule.min}개 넣어 주세요.`,
    });
  }

  const allowedTypes = BUTTON_TYPES.map((type) => type.toUpperCase());
  list.forEach((button, index) => {
    const type = buttonType(button);
    const itemField = `${field}[${index}]`;
    if (!allowedTypes.includes(type)) {
      errors.push({
        field: `${itemField}.type`,
        code: "BM_BUTTON_TYPE_INVALID",
        message: `${label} ${index + 1}번의 종류를 선택해 주세요.`,
      });
    }
    if (!button?.name) {
      errors.push({
        field: `${itemField}.name`,
        code: "BM_BUTTON_NAME_REQUIRED",
        message: `${label} ${index + 1}번의 이름을 입력해 주세요.`,
      });
    } else if (length(button.name) > rule.nameMax) {
      errors.push({
        field: `${itemField}.name`,
        code: "BM_BUTTON_NAME_TOO_LONG",
        message: `${label} 이름은 ${rule.nameMax}자까지 넣을 수 있어요.`,
      });
    }
    if (["WL", "AL"].includes(type)) {
      if (!button?.linkMo) {
        errors.push({
          field: `${itemField}.linkMo`,
          code: "BM_BUTTON_LINK_REQUIRED",
          message: `${label} ${index + 1}번의 모바일 링크를 입력해 주세요.`,
        });
      }
      // PC 링크는 선택이지만(figma 3497:43993 — 별표 없는 입력칸) 형식은 맞아야 한다.
      // §5·서버 모두 버튼 링크를 검증하지 않아 프론트가 유일한 방어선이고, 스킴이
      // 빠지면 카카오가 발송 시점에 7101 형식 오류로 떨군다(운영 실측,
      // brand-message-issue.md) — 그 실패는 비동기라 화면에 안 뜬다.
      // AC 에는 걸지 않는다. 링크 입력칸이 없는 유형이라 값이 남아 있어도 지울
      // 경로가 없어 해소 불가능한 에러가 된다.
      validateWebLink(errors, button?.linkPc, {
        field: `${itemField}.linkPc`,
        label: `${label} ${index + 1}번의 PC 링크`,
        code: "BM_BUTTON_LINK_INVALID",
      });
    }
    if (type === "AC" && !canUseChannelAddButton(sendTarget)) {
      errors.push({
        field: `${itemField}.type`,
        code: "BM_AC_NOT_ALLOWED",
        message:
          "채널 추가 버튼은 '채널 친구가 아닌 고객'·'마케팅 동의 고객 전체'로 보낼 때만 쓸 수 있어요.",
      });
    }
    if (type === "AC" && rule.allowChannelAdd === false) {
      errors.push({
        field: `${itemField}.type`,
        code: "BM_AC_NOT_ALLOWED",
        message: `${label}에는 채널 추가 버튼을 넣을 수 없어요.`,
      });
    }
  });

  const acIndexes = list.reduce(
    (acc, button, index) => (isChannelAddButton(button) ? [...acc, index] : acc),
    [],
  );
  if (acIndexes.length > 1) {
    errors.push({
      field,
      code: "BM_AC_LIMIT",
      message: "채널 추가 버튼은 1개만 넣을 수 있어요.",
    });
  } else if (acIndexes.length === 1 && rule.acPosition) {
    const expected = rule.acPosition === "last" ? list.length - 1 : 0;
    if (acIndexes[0] !== expected) {
      errors.push({
        field: `${field}[${acIndexes[0]}].type`,
        code: "BM_AC_POSITION_INVALID",
        message:
          rule.acPosition === "last"
            ? "채널 추가 버튼은 마지막에 놓아야 해요."
            : "채널 추가 버튼은 맨 앞에 놓아야 해요.",
      });
    }
  }
};

const validateMessageBody = (errors, value, limits, { field, label, code }) => {
  if (!value) {
    errors.push({ field, code, message: `${label}을 입력해 주세요.` });
    return;
  }
  if (length(value) > limits.message) {
    errors.push({
      field,
      code,
      message: `${label}은 ${limits.message.toLocaleString()}자까지 넣을 수 있어요.`,
    });
  }
  if (limits.newline != null && countNewlines(value) > limits.newline) {
    errors.push({
      field,
      code,
      message: `${label}의 줄바꿈은 ${limits.newline}번까지 넣을 수 있어요.`,
    });
  }
};

/**
 * 발송 전 프론트 검증. §6-1 의 "차단 ✅" 항목을 서버 왕복 없이 먼저 막는다.
 * 반환 {ok, errors:[{field, message, code}]} — field 는 화면이 스크롤할 수 있는
 * 경로(`cards[2].message`), code 는 대응하는 서버 에러 키(§6-1)다.
 *
 * options
 *  - profile               LS_getKakaoProfile 응답. 없으면 승인/080 검사를 건너뛴다
 *  - brandMessageApproved  profile 대신 직접 넘기는 승인 여부
 *  - unsubscribePhoneNumber profile 대신 직접 넘기는 080 번호
 *  - auto                  자동 캠페인(발송창 1시간 일찍 마감). 기본값은 campaign.type
 *  - now                   기준 시각(테스트 주입용)
 *  - checkSchedule         false 면 발송 시각 관련 검사를 건너뛴다(내용만 검증할 때)
 *  - couponsById           연결 쿠폰 레코드 맵 {[couponId]: coupon}. 자체 바코드 쿠폰
 *                          (getBarcodeCouponsById) + 자사몰 쿠폰(getProviderCouponsById)을
 *                          합쳐 넘긴다. 없거나 비어 있으면 삭제·비바코드 판정을 건너뛴다
 *                          — validateLinkedCoupon 주석 참고
 *
 * 승인·080 은 "미확인(프로필 미조회)"과 "없음"을 구분한다 — 프로필을 아직 못
 * 읽었을 때 붉은 에러를 띄우지 않기 위해서다(응답에 없는 키는 undefined 로 온다).
 *
 * 본문 길이는 (광고) 프리픽스를 뺀 실효 상한이 아니라 **원본 상한**으로 잰다.
 * 서버 검증과 어긋나면 보낼 수 있는 메시지를 프론트가 막게 된다 — 실효 상한은
 * getEffectiveMessageLimit 로 카운터에서만 안내한다.
 */
export const validateBrandCampaign = (campaign = {}, options = {}) => {
  const errors = [];
  const {
    profile,
    brandMessageApproved,
    unsubscribePhoneNumber,
    now,
    checkSchedule = true,
    auto = campaign.type === "auto",
    couponsById,
  } = options;

  const messageType = campaign.messageType;
  const type = getMessageType(messageType);
  const sendTarget = campaign.sendTarget || "friend";

  if (!type) {
    errors.push({
      field: "messageType",
      code: "BM_TYPE_UNSUPPORTED",
      message: "메시지 유형을 선택해 주세요.",
    });
  } else if (type.serverPending) {
    // serverPending 유형(현재 없음) — 플래그가 켜진 유형은 서버가 거절하므로 여기서 먼저 막는다.
    errors.push({
      field: "messageType",
      code: "BM_TYPE_UNSUPPORTED",
      message: "아직 보낼 수 없는 메시지 유형이에요.",
    });
  }
  if (!SEND_TARGET_KEYS.includes(sendTarget)) {
    errors.push({
      field: "sendTarget",
      code: "BM_TARGET_INVALID",
      message: "받는 대상을 선택해 주세요.",
    });
  }

  if (isMarketingTarget(sendTarget)) {
    const approved = brandMessageApproved ?? profile?.brandMessage;
    const unsubscribe =
      unsubscribePhoneNumber ?? profile?.unsubscribePhoneNumber;
    const profileKnown =
      profile != null ||
      brandMessageApproved !== undefined ||
      unsubscribePhoneNumber !== undefined;
    if (profileKnown && approved !== true) {
      errors.push({
        field: "sendTarget",
        code: "BM_NOT_APPROVED",
        message:
          "브랜드 메시지 사용 신청이 승인된 뒤에 친구가 아닌 고객에게 보낼 수 있어요.",
      });
    }
    if (profileKnown && !unsubscribe) {
      errors.push({
        field: "sendTarget",
        code: "BM_UNSUBSCRIBE_NUMBER_MISSING",
        message: "카카오 채널에 080 무료수신거부 번호를 먼저 등록해 주세요.",
      });
    }
  }

  // 본문형(ut·ui·uw) — 본문 글자수 + 줄바꿈
  if (type?.limits?.message != null) {
    validateMessageBody(errors, campaign.message, type.limits, {
      field: "message",
      label: "내용",
      code: "BM_MESSAGE_INVALID",
    });
  }

  // 이미지 — ut 는 image 가 없다(§5)
  if (type?.image?.required && !hasImage(campaign.image)) {
    errors.push({
      field: "image",
      code: "BM_IMAGE_REQUIRED",
      message: "사진을 넣어 주세요.",
    });
  }
  if (type?.image) {
    validateWebLink(errors, campaign.imageLink, {
      field: "imageLink",
      label: "이미지 클릭 링크",
      code: "BM_IMAGE_LINK_INVALID",
    });
  }

  // 캠페인 레벨 버튼 — uc 는 buttons 규칙이 null 이라 "사용 불가"로 걸린다.
  // 상한은 쿠폰 ON 여부를 반영한 실효값(BM-78) — 서버가 같은 값으로 거절한다.
  if (type && type.key !== "uc") {
    const couponOn = hasCouponInput(campaign.coupon, campaign.couponId);
    validateButtons(
      errors,
      campaign.buttons,
      type.buttons && { ...type.buttons, max: getButtonMax(type.key, { couponOn }) },
      { sendTarget, field: "buttons", label: "버튼" },
    );
  }

  // 쿠폰 — 버튼이 아니라 campaign.coupon 오브젝트다(§5). 캐러셀은 cards[i].coupon 이라
  // 아래 uc 블록이 카드마다 본다.
  if (type && type.key !== "uc") {
    validateCoupon(errors, campaign.coupon, {
      messageType,
      couponId: campaign.couponId,
      couponProvider: campaign.couponProvider,
      couponsById,
    });
  }

  if (type?.key === "ul") {
    const max = type.limits.title;
    if (!campaign.title) {
      errors.push({
        field: "title",
        code: "BM_LIST_HEADER_INVALID",
        message: "제목을 입력해 주세요.",
      });
    } else if (length(campaign.title) > max) {
      errors.push({
        field: "title",
        code: "BM_LIST_HEADER_INVALID",
        message: `제목은 ${max}자까지 넣을 수 있어요.`,
      });
    }

    const items = campaign.listItems || [];
    const rule = type.listItems;
    if (items.length < rule.min || items.length > rule.max) {
      errors.push({
        field: "listItems",
        code: "BM_LIST_ITEM_COUNT",
        message: `항목은 대표를 포함해 ${rule.min}~${rule.max}개까지 넣을 수 있어요.`,
      });
    }
    items.forEach((item, index) => {
      const titleMax = index === 0 ? rule.firstTitleMax : rule.titleMax;
      // §5: 메인(0번) 타이틀은 선택, 서브 타이틀은 필수
      const titleRequired =
        index === 0 ? rule.firstTitleRequired !== false : rule.titleRequired;
      if (!item?.title) {
        if (titleRequired) {
          errors.push({
            field: `listItems[${index}].title`,
            code: "BM_LIST_ITEM_TITLE_INVALID",
            message: `${index + 1}번 항목의 내용을 입력해 주세요.`,
          });
        }
      } else {
        if (length(item.title) > titleMax) {
          errors.push({
            field: `listItems[${index}].title`,
            code: "BM_LIST_ITEM_TITLE_INVALID",
            message: `${index + 1}번 항목의 내용은 ${titleMax}자까지 넣을 수 있어요.`,
          });
        }
        if (
          rule.titleNewline != null &&
          countNewlines(item.title) > rule.titleNewline
        ) {
          errors.push({
            field: `listItems[${index}].title`,
            code: "BM_LIST_ITEM_TITLE_INVALID",
            message: `${index + 1}번 항목의 줄바꿈은 ${rule.titleNewline}번까지 넣을 수 있어요.`,
          });
        }
      }
      if (!hasImage(item?.image)) {
        errors.push({
          field: `listItems[${index}].image`,
          code: "BM_LIST_ITEM_IMAGE_REQUIRED",
          message: `${index + 1}번 항목의 사진을 넣어 주세요.`,
        });
      }
      // 링크는 Mobile 필수 · PC 선택(§5). 서버는 아직 검증하지 않는다.
      validateWebLink(errors, item?.linkMo, {
        field: `listItems[${index}].linkMo`,
        label: `${index + 1}번 항목의 모바일 링크`,
        code: "BM_LIST_ITEM_LINK_REQUIRED",
        required: rule.linkMoRequired,
      });
      validateWebLink(errors, item?.linkPc, {
        field: `listItems[${index}].linkPc`,
        label: `${index + 1}번 항목의 PC 링크`,
        code: "BM_LIST_ITEM_LINK_INVALID",
      });
    });
  }

  if (type?.key === "uc") {
    const cards = campaign.cards || [];
    const rule = type.cards;
    if (cards.length < rule.min || cards.length > rule.max) {
      errors.push({
        field: "cards",
        code: "BM_CARD_COUNT",
        message: `카드는 ${rule.min}~${rule.max}개까지 넣을 수 있어요.`,
      });
    }
    cards.forEach((card, index) => {
      if (!card?.header) {
        errors.push({
          field: `cards[${index}].header`,
          code: "BM_CARD_HEADER_INVALID",
          message: `${index + 1}번 카드의 제목을 입력해 주세요.`,
        });
      } else if (length(card.header) > rule.headerMax) {
        errors.push({
          field: `cards[${index}].header`,
          code: "BM_CARD_HEADER_INVALID",
          message: `${index + 1}번 카드의 제목은 ${rule.headerMax}자까지 넣을 수 있어요.`,
        });
      }
      validateMessageBody(
        errors,
        card?.message,
        { message: rule.messageMax, newline: rule.messageNewline },
        {
          field: `cards[${index}].message`,
          label: `${index + 1}번 카드의 내용`,
          code: "BM_CARD_MESSAGE_INVALID",
        },
      );
      if (!hasImage(card?.image)) {
        errors.push({
          field: `cards[${index}].image`,
          code: "BM_CARD_IMAGE_REQUIRED",
          message: `${index + 1}번 카드의 사진을 넣어 주세요.`,
        });
      }
      validateWebLink(errors, card?.imageLink, {
        field: `cards[${index}].imageLink`,
        label: `${index + 1}번 카드의 이미지 클릭 링크`,
        code: "BM_IMAGE_LINK_INVALID",
      });
      // BM-40: 카드 버튼은 1~2개 필수 — rule.buttons.min 이 0개를 막는다.
      validateButtons(errors, card?.buttons, rule.buttons, {
        sendTarget,
        field: `cards[${index}].buttons`,
        label: `${index + 1}번 카드 버튼`,
      });
    });

    // 쿠폰은 카드마다 독립(cards[i].coupon) — 연결 쿠폰(cards[i].coupon.couponId)도
    // 카드별이라(BM-86) 링크·레코드 검사를 카드마다 그 카드의 couponId 로 받는다. 같은
    // 쿠폰을 여러 카드에 연결했으면 레코드 에러(삭제·비바코드)가 카드마다 나온다 —
    // 카드별 선택이라 고칠 자리도 카드마다다. 캠페인 레벨 coupon·couponId 는 읽지 않는다
    // — couponId 는 전환기 이중 기록(첫 카드 값)이라 폼에서 연결을 전부 풀어도 남는데,
    // 여기서 hydrateBrandCoupon 을 태우면 옛 저장분으로 오판해 되살아난다. 폼을 거치지
    // 않은 옛 저장분(캠페인 목록의 활성화 게이트)은 호출부(MessageCampaign)가 먼저 태운다.
    (cards || []).forEach((card, index) => {
      if (!hasCouponInput(card?.coupon)) return;
      validateCoupon(errors, card.coupon, {
        messageType,
        couponId: card.coupon?.couponId,
        couponProvider: card.coupon?.couponProvider,
        couponsById,
        field: `cards[${index}].coupon`,
        index: index + 1,
        label: `${index + 1}번 카드 쿠폰`,
      });
    });

    // 캐러셀은 카드 전체를 통틀어 채널 추가 버튼 1개만 허용
    const acCount = cards.reduce(
      (count, card) =>
        count + (card?.buttons || []).filter(isChannelAddButton).length,
      0,
    );
    if (acCount > 1) {
      errors.push({
        field: "cards",
        code: "BM_CAROUSEL_AC_LIMIT",
        message: "채널 추가 버튼은 카드 전체를 통틀어 1개만 넣을 수 있어요.",
      });
    }
    if ((campaign.buttons || []).length) {
      errors.push({
        field: "buttons",
        code: "BM_BUTTON_LIMIT",
        message: "캐러셀은 카드마다 버튼을 넣어요. 공통 버튼은 뺄게요.",
      });
    }
    // "더보기"(tail) 자체는 선택이지만 켰으면 모바일 링크가 필수다(BM-81 — 웹 가이드
    // "더보기 설정 시 Mobile 링크를 반드시 입력"). 서버는 토글을 모르므로 PC 만 있고
    // 모바일이 빈 tail 을 '켰는데 비운 것'으로 보고 1단계·테스트 모두 거절한다.
    if (campaign.tail && isBlankValue(campaign.tail.linkMo)) {
      errors.push({
        field: "tail.linkMo",
        code: "BM_TAIL_LINK_REQUIRED",
        message: "더보기를 켰으면 모바일 링크를 넣어 주세요.",
      });
    } else {
      validateWebLink(errors, campaign.tail?.linkMo, {
        field: "tail.linkMo",
        label: "더보기 모바일 링크",
        code: "BM_TAIL_LINK_INVALID",
      });
    }
    validateWebLink(errors, campaign.tail?.linkPc, {
      field: "tail.linkPc",
      label: "더보기 PC 링크",
      code: "BM_TAIL_LINK_INVALID",
    });
  }

  if (!checkSchedule) return { ok: errors.length === 0, errors };

  if (campaign.type === "auto") {
    // 자동 브랜드 캠페인은 발송 시각(sendAtTime)이 필수다. 즉시·딜레이 발송은
    // 서버가 거부한다.
    if (!campaign.sendAtTime) {
      errors.push({
        field: "sendAtTime",
        code: "BM_AUTO_SEND_TIME_REQUIRED",
        message: "보낼 시각을 정해 주세요.",
      });
    } else if (!isSendableTime(campaign.sendAtTime, { auto: true })) {
      errors.push({
        field: "sendAtTime",
        code: "BM_OUT_OF_SEND_WINDOW",
        message: getSendWindowMessage({ auto: true }),
      });
    }
  } else if (campaign.sendNow) {
    if (!isSendableTime(now || new Date(), { auto })) {
      errors.push({
        field: "sendNow",
        code: "BM_OUT_OF_SEND_WINDOW",
        message: getSendWindowMessage({ auto }),
      });
    }
  } else if (!campaign.reserveDate) {
    errors.push({
      field: "reserveDate",
      code: "BM_RESERVE_DATE_REQUIRED",
      message: "보낼 날짜와 시각을 정해 주세요.",
    });
  } else if (!isSendableTime(campaign.reserveDate, { auto })) {
    errors.push({
      field: "reserveDate",
      code: "BM_OUT_OF_SEND_WINDOW",
      message: getSendWindowMessage({ auto }),
    });
  } else if (!isReservableDate(campaign.reserveDate, { auto, now })) {
    errors.push({
      field: "reserveDate",
      code: "BM_RESERVE_TOO_FAR",
      message: `예약 발송은 ${RESERVE_MAX_DAYS}일 이내로만 잡을 수 있어요.`,
    });
  }

  return { ok: errors.length === 0, errors };
};

// ─────────────────────────────────────────────────────────────
// 페이로드
// ─────────────────────────────────────────────────────────────

/** 저장 shape 은 {key, level}. level 은 서버 기본값이 없어 필수다. */
const normalizeImage = (image) =>
  image?.key ? { key: image.key, level: image.level || "public" } : null;

/**
 * CampaignButton.order 는 Int! 라 인덱스를 반드시 채운다.
 * AC 는 유형 규칙 위치로 옮기고(BM-41 ②) 이름을 "채널 추가"로 고정한다(BM-41 ①).
 * BM-29 이전 캠페인을 다시 저장할 때 남아 있는 쿠폰 버튼은 떨군다 — 서버는
 * 쿠폰 오브젝트(campaign.coupon / cards[i].coupon)로만 쿠폰을 조립한다(§5).
 */
const normalizeButtons = (buttons, messageType) => {
  const list = orderButtonsForType(buttons, messageType).filter(
    (button) => button && button.type && !isLegacyCouponButton(button),
  );
  if (!list.length) return null;
  return list.map((button, index) => ({
    order: index,
    type: button.type,
    name: isChannelAddButton(button)
      ? CHANNEL_ADD_BUTTON_NAME
      : button.name || null,
    linkMo: button.linkMo || null,
    linkPc: button.linkPc || null,
    target: button.target || null,
  }));
};

/**
 * 저장 shape 은 §5 그대로 {type, value, description, linkMo, linkPc, schemeAndroid,
 * schemeIos} + 연결 쿠폰 {couponId, couponProvider} 다(BM-86 — 캐러셀 카드 쿠폰이 쓴다.
 * 본문형·ul 의 campaign.coupon 에는 값이 없어 null 로 나간다). title 은 type+value 로
 * 서버가 조립하므로 싣지 않는다.
 * value 는 숫자형이면 Number, free·up 은 상품명 문자열, shipping 은 null 이다.
 * 유형이 5종이 아니면 통째로 null — 반쪽짜리 오브젝트를 보내면 서버가 title 을 못 만든다
 * (검증에서 BM_COUPON_TYPE_INVALID 로 먼저 막힌다). 단 연결 쿠폰(couponId)이 있으면
 * 나머지를 null 로 비운 오브젝트를 남긴다 — 캐러셀은 연결 쿠폰의 자리가 이 오브젝트뿐이라
 * (본문형·ul 의 캠페인 레벨 couponId 와 달리) 통째로 버리면 "연결만 하고 유형은 아직"인
 * 초안 저장에서 연결이 사라지고, 이중 기록(campaign.couponId)만 남아 카드와 어긋난다.
 * 발송은 유형 필수 검증(프론트·서버 BM_COUPON_TYPE_INVALID:N 모두)이 먼저 막는다.
 */
const normalizeCoupon = (coupon) => {
  const type = getCouponType(coupon?.type);
  if (!type) {
    return coupon?.couponId
      ? {
          type: null,
          value: null,
          description: null,
          linkMo: null,
          linkPc: null,
          schemeAndroid: null,
          schemeIos: null,
          couponId: coupon.couponId,
          couponProvider: coupon.couponProvider || null,
        }
      : null;
  }
  const rule = type.value;
  let value = null;
  if (rule.kind === "number") {
    const number = Number(String(coupon.value ?? "").replace(/,/g, ""));
    value = Number.isFinite(number) ? number : null;
  } else if (rule.kind === "text") {
    value = coupon.value ? String(coupon.value).trim() : null;
  }
  return {
    type: type.key,
    value,
    description: coupon.description || null,
    // 연결 쿠폰(couponId)이 있으면 서버가 수신자별 쿠폰 링크로 채운다 — 비어도 된다.
    linkMo: coupon.linkMo || null,
    linkPc: coupon.linkPc || null,
    schemeAndroid: coupon.schemeAndroid || null,
    schemeIos: coupon.schemeIos || null,
    // 카드별 연결 쿠폰(BM-86). couponProvider 는 couponId 와 한 쌍이다 — 자사몰 쿠폰의
    // 키는 providerCouponId 라 provider 없이 저장하면 서버가 자체 쿠폰 id 로 읽는다.
    couponId: coupon.couponId || null,
    couponProvider: coupon.couponId ? coupon.couponProvider || null : null,
  };
};

const normalizeTail = (tail) =>
  tail && (tail.linkMo || tail.linkPc)
    ? { linkMo: tail.linkMo || null, linkPc: tail.linkPc || null }
    : null;

/**
 * 폼 상태 → Campaign 저장 필드.
 *
 *  - (광고) 표기·무료수신거부 문구는 넣지 않는다. 서버가 발송 직전 합성한다.
 *  - 유형 전환 시 이전 유형 필드가 남으면 서버 검증·발송이 어긋나므로 관계없는
 *    필드는 항상 null 로 덮어쓴다. ut 는 이미지가 없어 image·imageLink 가 null 이다.
 *  - 쿠폰은 buttons 가 아니라 coupon 오브젝트로 나간다(§5). 캐러셀은 cards[i].coupon
 *    카드마다(연결 쿠폰 포함) — uc 분기 주석.
 *  - couponId·couponProvider(연결 쿠폰 — 서버가 그걸로 수신자별 linkMo 를 채운다)는
 *    본문형·ul 이면 폼의 캠페인 레벨 값 그대로, 캐러셀이면 **연결 쿠폰이 있는 첫 카드의
 *    값**(전환기 이중 기록, BM-86)이다.
 *  - listItems/cards/tail/coupon 은 객체 그대로 돌려준다. AWSJSON 직렬화는 API 계층 책임.
 */
export const buildBrandMessagePayload = (form = {}) => {
  const messageType = MESSAGE_TYPE_KEYS.includes(form.messageType)
    ? form.messageType
    : "ui";
  const sendTarget = SEND_TARGET_KEYS.includes(form.sendTarget)
    ? form.sendTarget
    : "friend";

  const payload = {
    method: CAMPAIGN_METHOD,
    messageType,
    sendTarget,
    brandMessageType: BRAND_MESSAGE_TYPE,
    message: null,
    title: null,
    image: null,
    imageLink: null,
    listItems: null,
    cards: null,
    tail: null,
    buttons: null,
    coupon: null,
    couponId: form.couponId || null,
    couponProvider: form.couponId ? form.couponProvider || null : null,
  };

  if (messageType === "ut") {
    payload.message = form.message || "";
    payload.buttons = normalizeButtons(form.buttons, messageType);
    payload.coupon = normalizeCoupon(form.coupon);
  }

  if (messageType === "ui" || messageType === "uw") {
    payload.message = form.message || "";
    payload.image = normalizeImage(form.image);
    payload.imageLink = form.imageLink || null;
    payload.buttons = normalizeButtons(form.buttons, messageType);
    payload.coupon = normalizeCoupon(form.coupon);
  }

  if (messageType === "ul") {
    // ul 은 title 을 리스트 헤더로 재사용한다.
    payload.title = form.title || "";
    payload.listItems = (form.listItems || []).map((item) => ({
      title: item?.title || "",
      image: normalizeImage(item?.image),
      linkMo: item?.linkMo || null,
      linkPc: item?.linkPc || null,
    }));
    payload.buttons = normalizeButtons(form.buttons, messageType);
    payload.coupon = normalizeCoupon(form.coupon);
  }

  if (messageType === "uc") {
    payload.cards = (form.cards || []).map((card) => ({
      header: card?.header || "",
      message: card?.message || "",
      image: normalizeImage(card?.image),
      imageLink: card?.imageLink || null,
      buttons: normalizeButtons(card?.buttons, messageType) || [],
      // 카드마다 독립(carousel.list[i].attachment.coupon). 없으면 null.
      coupon: normalizeCoupon(card?.coupon),
    }));
    payload.tail = normalizeTail(form.tail);
    // 캐러셀은 카드 버튼만 쓴다. 캠페인 레벨 buttons 를 남기면 서버가 그대로
    // wire 에 실어 보낸다.
    payload.buttons = null;
    // 캐러셀 쿠폰은 cards[i].coupon 뿐이다(BM-85). campaign.coupon 에
    // 값을 남기면 카드 쿠폰이 하나도 없을 때 서버가 옛 계약 폴백으로 전 카드에 복제한다
    // — 항상 null 로 싣는다.
    payload.coupon = null;
    // 전환기 이중 기록(BM-86): 연결 쿠폰은 카드별(cards[i].coupon.couponId)이지만 서버가
    // 카드별 발급을 배포하기 전까지는 campaign.couponId 하나를 발급해 전 카드에 같은
    // 링크를 준다 — 연결 쿠폰이 있는 첫 카드의 값을 캠페인 레벨에도 싣어 종전 동작을
    // 유지한다. 폼의 캠페인 레벨 값은 읽지 않는다(편집 중 카드와 어긋난다). 정규화된
    // payload.cards 에서 뽑는다 — 폼(getLinkedCoupons)에서 뽑으면 normalizeCoupon 이 버린
    // 카드의 couponId 가 캠페인 레벨에만 남아 저장분 안에서 카드와 어긋난다.
    // 제거 조건: 서버가 카드 쿠폰에 couponId 가 있으면 campaign.couponId 를 무시하도록
    // 배포(docs/brand-message-issue.md BM-86 ②)된 뒤 — 그때 null 로 싣는다.
    const firstLinked =
      payload.cards.find((card) => card.coupon?.couponId)?.coupon || null;
    payload.couponId = firstLinked?.couponId || null;
    payload.couponProvider = firstLinked?.couponProvider || null;
  }

  return payload;
};
